import { apiGet, apiPost, apiPut, apiDelete, unwrap } from './apiClient';
import { ResponseDTO, CreatePolicyRequest, PolicyDetails, MdmPolicy } from '@/types/api';

const BASE = '/api/v1/apple/mdm/policies';

export async function createPolicy(request: CreatePolicyRequest): Promise<{ id: string }> {
  const body = serializePolicyRequest(request);
  const res = await apiPost<ResponseDTO<{ id: string }>>(BASE, body);
  return unwrap(res);
}

/**
 * Strip empty optionals from a policy create/update request so the wire
 * shape matches the legacy portal's contract:
 *   { name, platforms: [], payloads: [...] }
 * Optional fields (description, tenantIds, allTenants, isDraft) are only
 * included when the user explicitly populated them. `platforms` is always
 * sent — defaulting to [] — because the backend treats a missing key
 * differently from an empty list.
 */
function serializePolicyRequest(req: CreatePolicyRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: req.name,
    platforms: req.platforms ?? [],
    payloads: req.payloads,
  };
  if (req.description) body.description = req.description;
  if (req.tenantIds && req.tenantIds.length > 0) body.tenantIds = req.tenantIds;
  if (req.allTenants) body.allTenants = true;
  if (req.isDraft) body.isDraft = true;
  return body;
}

export async function listPolicies(): Promise<PolicyDetails[]> {
  const res = await apiGet<ResponseDTO<PolicyDetails[]>>(BASE);
  return unwrap(res);
}

export async function getPolicy(id: string): Promise<PolicyDetails> {
  const res = await apiGet<ResponseDTO<PolicyDetails>>(`${BASE}/${id}`);
  return unwrap(res);
}

export async function updatePolicy(id: string, request: CreatePolicyRequest): Promise<string> {
  const body = serializePolicyRequest(request);
  const res = await apiPut<ResponseDTO<null>>(`${BASE}/${id}`, body);
  return res.message || 'Policy updated';
}

export async function deletePolicy(id: string): Promise<string> {
  const res = await apiDelete<ResponseDTO<null>>(`${BASE}/${id}`);
  return res.message || 'Policy deleted';
}

export async function listCertificatePolicies(): Promise<MdmPolicy[]> {
  const res = await apiGet<ResponseDTO<MdmPolicy[]>>(`${BASE}/certificates`);
  return unwrap(res);
}

export async function getSubstitutionVariables(): Promise<string[]> {
  const res = await apiGet<ResponseDTO<string[]>>(`${BASE}/substitution-variables`);
  return unwrap(res);
}
